import { useContext } from 'react';

import en from '@/i18n/en.json';
import sw from '@/i18n/sw.json';
import { LanguageContext, type Language } from '@/contexts/LanguageContext';

type Resource = Record<string, unknown>;

const RESOURCES: Record<Language, Resource> = {
  en: en as unknown as Resource,
  sw: sw as unknown as Resource,
};

function getNestedValue(obj: Resource, path: string): string | undefined {
  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return typeof current === 'string' ? current : undefined;
}

/** Replace `{{name}}` placeholders with the matching param value. */
function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key: string) =>
    key in params ? String(params[key]) : match,
  );
}

/**
 * Resolve a translation key for the given language.
 *
 * Resolution order: active language → English fallback → the key string itself.
 * Pure (no React) so it is unit-testable without rendering. Supports `{{var}}`
 * interpolation for dynamic values.
 */
export function translate(
  lang: Language,
  key: string,
  params?: Record<string, string | number>,
): string {
  const active = getNestedValue(RESOURCES[lang], key);
  if (active !== undefined) return interpolate(active, params);
  const fallback = getNestedValue(RESOURCES.en, key);
  if (fallback !== undefined) return interpolate(fallback, params);
  return key;
}

/**
 * Language-aware translation hook. Keeps the original `t('key')` signature so
 * existing call sites (the guard surface) work unchanged, and adds optional
 * interpolation params: `t('greeting', { name })`.
 */
export function useTranslation() {
  // Read the context directly (not via useLanguage) so the hook still works in
  // tests/components rendered without a LanguageProvider — it falls back to
  // English rather than throwing. When the provider is present, it is reactive.
  const ctx = useContext(LanguageContext);
  const lang: Language = ctx?.lang ?? 'en';
  const t = (key: string, params?: Record<string, string | number>): string =>
    translate(lang, key, params);
  return { t, lang };
}
